/**
 * Tanıtım sitesindeki blog bölümünün statik içerik kaynağı.
 * CMS yok — makaleler burada tutulur, /blog ve /blog/:slug sayfaları
 * bu diziyi okur. Yeni yazı eklemek için diziye bir kayıt ekleyin.
 *
 * body: HTML string (v-html ile basılır). Yalnızca bu dosyadaki
 * güvenilir içerik render edildiği için kullanıcı girdisi riski yoktur.
 */

export interface IBlogPost {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  date: string; // ISO
  readMinutes: number;
  icon: string; // mdi ikon adı
  cover: string; // kapak için CSS gradient
  body: string;
}

export const blogCategories = [
  "Tümü",
  "Başlangıç",
  "Büyüme",
  "Para Kazanma",
  "İş Birlikleri",
  "Vergi & Mevzuat",
];

export const blogPosts: IBlogPost[] = [
  {
    slug: "affiliate-marketing-nedir-influencer-rehberi",
    title: "Affiliate Marketing Nedir? Influencer'lar İçin Sıfırdan Rehber",
    excerpt:
      "Komisyon nasıl hesaplanır, çerez süresi ne demek, ilk satışını nasıl alırsın? Affiliate iş modelinin tüm temellerini tek yazıda topladık.",
    category: "Başlangıç",
    date: "2026-08-12",
    readMinutes: 9,
    icon: "mdi-link-variant",
    cover: "linear-gradient(140deg,#f85617,#b32d05)",
    body: `
<p><strong>Affiliate marketing</strong> (satış ortaklığı), bir markanın ürününü kendi kitlene önerip, senin linkin üzerinden gerçekleşen her satıştan komisyon kazandığın gelir modelidir. Reklam vermezsin, stok tutmazsın, kargo ile uğraşmazsın. Tek yaptığın: doğru ürünü, doğru kitleye, izlenebilir bir link ile önermek.</p>

<p>Türkiye'de son yıllarda içerik üreticilerinin en istikrarlı gelir kalemi haline geldi. Sebebi basit: marka iş birlikleri düzensizdir, ayda bir gelir bir gelmez. Affiliate ise <strong>bir kez ürettiğin içeriğin aylarca kazandırmaya devam etmesi</strong> demektir.</p>

<h2>Zincir nasıl işliyor?</h2>

<p>Süreç dört halkadan oluşur ve her halkanın kendi teknik karşılığı vardır:</p>

<ol>
  <li><strong>Link üretimi.</strong> Markanın ürün sayfasını, sana özel bir takip kodu içeren kısa bir linke dönüştürürsün. Bu link senin kimliğindir.</li>
  <li><strong>Tıklama.</strong> Takipçin linke tıkladığında tarayıcısına bir <em>çerez</em> (cookie) yazılır. Bu çerez "bu ziyaretçiyi şu içerik üreticisi getirdi" bilgisini taşır.</li>
  <li><strong>Dönüşüm.</strong> Ziyaretçi çerez süresi içinde alışveriş yaparsa satış sana yazılır.</li>
  <li><strong>Hakediş.</strong> Marka satışı onaylar (iade süresi dolduktan sonra), komisyon hesabına düşer, hak edişin fatura dönemine bağlanır.</li>
</ol>

<h2>Çerez süresi neden bu kadar önemli?</h2>

<p>Çerez süresi, tıklamadan sonra satışın sana yazılmaya devam edeceği süredir. Programdan programa değişir; kimi markada 24 saat, kimisinde 30 gündür. Pratikte şu anlama gelir:</p>

<blockquote><p>Takipçin senin linkine sabah tıklayıp akşam sepeti tamamladıysa — çerez süresi yeterliyse — komisyon yine senindir. Ama araya başka bir içerik üreticisinin linki girerse çoğu programda <strong>son tıklama</strong> kazanır.</p></blockquote>

<p>Bu yüzden "linki story'ye koydum, olan oldu" yaklaşımı yerine, linki takipçinin satın alma anına <em>en yakın</em> noktaya yerleştirmek gerekir.</p>

<h2>Komisyon nasıl hesaplanır?</h2>

<p>Komisyon, ürünün <strong>KDV hariç satış tutarı</strong> üzerinden belirli bir yüzde olarak hesaplanır. Kategoriye göre değişir: kozmetik ve moda genelde yüksek, elektronik düşük oranlıdır.</p>

<div class="fl-prose__scroll">
<table>
  <thead>
    <tr><th>Kategori</th><th>Tipik komisyon</th><th>Sepet tutarı</th><th>1.000 tıklamada tahmini kazanç*</th></tr>
  </thead>
  <tbody>
    <tr><td>Kozmetik & kişisel bakım</td><td>%8 – 12</td><td>Düşük</td><td>Orta – yüksek</td></tr>
    <tr><td>Moda & giyim</td><td>%6 – 10</td><td>Orta</td><td>Yüksek</td></tr>
    <tr><td>Ev & yaşam</td><td>%4 – 8</td><td>Orta</td><td>Orta</td></tr>
    <tr><td>Elektronik</td><td>%1 – 3</td><td>Yüksek</td><td>Düşük – orta</td></tr>
    <tr><td>Süpermarket & hızlı tüketim</td><td>%2 – 5</td><td>Düşük</td><td>Düşük ama tekrarlı</td></tr>
  </tbody>
</table>
</div>

<p><em>*Oranlar sektördeki genel aralıklardır; markanın güncel oranı her zaman platformdaki marka sayfasında yazar.</em></p>

<h3>Basit bir hesap</h3>

<p>10.000 takipçili bir hesabın story'sini ortalama 800 kişi görür. Bunların %3'ü linke tıklarsa 24 tıklama eder. Tıklayanların %4'ü satın alırsa 1 satış çıkar. Sepet 900 TL ve komisyon %8 ise kazanç 72 TL'dir.</p>

<p>Tek story için düşük görünür. Ama aynı içeriği bir <strong>koleksiyon sayfasına</strong> koyup profil bio'ndan kalıcı olarak yayınlarsan, o link 30 gün boyunca tıklanmaya devam eder. Affiliate'te kazanç tek seferlik gösterimden değil, <strong>birikimden</strong> gelir.</p>

<h2>İlk 30 günde ne yapmalısın?</h2>

<ul>
  <li><strong>Gün 1–3:</strong> Profilini tamamla, çalışmak istediğin markalara başvur, onay bekleyen başvuruları takip et.</li>
  <li><strong>Gün 4–10:</strong> Gerçekten kullandığın 10 ürün belirle ve hepsi için link üret. Sahte öneri, dönüşümü öldürür.</li>
  <li><strong>Gün 11–20:</strong> Bu 10 ürünü tek bir koleksiyon sayfasında topla, profil bio'na yerleştir. Story'lerde bu tek linke yönlendir.</li>
  <li><strong>Gün 21–30:</strong> Raporlara bak. Hangi ürün tıklanıyor ama satmıyor? Fiyat itirazı vardır — kupon iste. Hangi ürün hiç tıklanmıyor? Görsel ya da anlatım zayıftır — içeriği değiştir.</li>
</ul>

<h2>Yeni başlayanların yaptığı 5 hata</h2>

<ol>
  <li><strong>Her ürüne link atmak.</strong> Kitlen 40 farklı ürün önerisini takip edemez; güven kaybolur, tıklama oranı düşer.</li>
  <li><strong>Ham linki paylaşmak.</strong> Takip kodu içermeyen link = satış var, komisyon yok.</li>
  <li><strong>Fiyat söylemek.</strong> Fiyatlar değişir, eski fiyat söyleyen içerik güvenilirliğini yitirir. "İndirimde" demek yeterli.</li>
  <li><strong>İş birliği etiketini atlamak.</strong> Ticari bağlantıyı belirtmek yasal bir zorunluluktur, aynı zamanda kitleyle aranı bozmaz — netlik güven getirir.</li>
  <li><strong>İlk hafta pes etmek.</strong> Affiliate gelirleri üstel büyür; ilk ay düşük olması normaldir.</li>
</ol>

<h2>Özet</h2>

<p>Affiliate marketing, içerik üreticiliğini bir gelir sistemine dönüştürmenin en ölçülebilir yoludur. Kazandıran şey takipçi sayın değil, <strong>önerinin inandırıcılığı ile linkin doğru yerde durması</strong>dır. Tıklamayı ve dönüşümü ölçebildiğin sürece, her ay bir öncekinden daha iyi bir sonuç almanın yolu açıktır.</p>
`,
  },

  {
    slug: "instagram-etkilesim-orani-artirma",
    title: "Instagram'da Etkileşim Oranını Artırmanın 12 Somut Yolu",
    excerpt:
      "Markalar artık takipçi sayısına değil etkileşim oranına bakıyor. Oranını nasıl hesaplarsın ve nasıl yükseltirsin — ölçülebilir taktiklerle.",
    category: "Büyüme",
    date: "2026-08-05",
    readMinutes: 8,
    icon: "mdi-chart-timeline-variant",
    cover: "linear-gradient(140deg,#ffb020,#e0630a)",
    body: `
<p>Bir markanın medya planlamacısı önüne gelen teklifte ilk baktığı sayı takipçi değildir. <strong>Etkileşim oranıdır (engagement rate).</strong> Çünkü takipçi satın alınabilir, etkileşim alınamaz.</p>

<h2>Önce doğru hesapla</h2>

<p>İki yaygın formül var; hangisini kullandığını teklifinde belirtmen profesyonel durur:</p>

<ul>
  <li><strong>Takipçiye göre ER:</strong> (Beğeni + Yorum + Kaydetme + Paylaşım) ÷ Takipçi × 100</li>
  <li><strong>Erişime göre ER:</strong> (Beğeni + Yorum + Kaydetme + Paylaşım) ÷ Erişim × 100</li>
</ul>

<p>Erişime göre hesap daha dürüsttür ve markalar bunu tercih eder. Kaba bir kıyas için: mikro hesaplarda (%10 bin altı) <strong>%4–8</strong> iyi, orta ölçekte <strong>%2–4</strong> sağlıklı, büyük hesaplarda <strong>%1,5 üzeri</strong> güçlü kabul edilir.</p>

<h2>12 taktik</h2>

<h3>İçerik tarafında</h3>

<ol>
  <li><strong>İlk 3 saniyeyi yeniden yaz.</strong> Reels'te izlenme süresini belirleyen tek şey açılıştır. "Merhaba arkadaşlar" ile başlayan videoyu kimse izlemez; sonucu ya da iddiayı başa al.</li>
  <li><strong>Kaydettirmeyi hedefle.</strong> Algoritmanın en çok değer verdiği sinyal kaydetmedir. Liste, karşılaştırma, "sakla lazım olur" formatları kaydedilir.</li>
  <li><strong>Carousel'in son karesine geri dönüş koy.</strong> "İlk kareye dön ve tekrar bak" tipi bir kapanış, ortalama görüntüleme süresini ciddi biçimde uzatır.</li>
  <li><strong>Bir formatı kovala, her hafta değiştirme.</strong> Kitle tanıdık formatı bekler; tanıdıklık etkileşimi büyütür.</li>
  <li><strong>Alt yazı ekle.</strong> Sesli izlenmeyen videoda mesaj kayboluyor; alt yazı tamamlanma oranını yükseltir.</li>
</ol>

<h3>Zamanlama ve dağıtım tarafında</h3>

<ol start="6">
  <li><strong>Kendi verinden saat seç.</strong> "En iyi paylaşım saati 20:00" gibi genel tavsiyeler kitlene uymayabilir; Insights → Takipçiler ekranındaki aktiflik grafiğini kullan.</li>
  <li><strong>Paylaşımdan sonraki 30 dakika sahada ol.</strong> Yorumlara ilk yarım saatte cevap vermek dağıtımı doğrudan etkiler.</li>
  <li><strong>Haftada 3 kaliteli gönderi, 7 vasat gönderiden iyidir.</strong> Düşük performanslı içerik hesabın ortalamasını aşağı çeker.</li>
</ol>

<h3>Topluluk tarafında</h3>

<ol start="9">
  <li><strong>Story'de anket ve soru kutusu kullan.</strong> Etkileşim maliyeti en düşük araçlardır ve DM trafiğini besler.</li>
  <li><strong>Yorumlara yorumla cevap ver, emoji ile değil.</strong> Tek kelimelik cevaplar algoritmik olarak sayılmaz denecek kadar zayıftır.</li>
  <li><strong>Takipçinin içeriğini paylaş.</strong> Ürünü kullanan takipçiyi story'de göstermek, hem sosyal kanıt hem etkileşim üretir.</li>
  <li><strong>Hayalet takipçiden kurtul.</strong> Hiç etkileşmeyen, aktif olmayan takipçiler oranını sürekli aşağı çeker; temizlemek sayını düşürür ama teklif gücünü yükseltir.</li>
</ol>

<h2>Markanın gerçekte baktığı 4 sayı</h2>

<div class="fl-prose__scroll">
<table>
  <thead><tr><th>Metrik</th><th>Nerede bulunur</th><th>Neden önemli</th></tr></thead>
  <tbody>
    <tr><td>Story erişimi</td><td>Insights → Story</td><td>İş birliği fiyatının temel çarpanı</td></tr>
    <tr><td>Reels ortalama izlenme</td><td>Insights → Reels</td><td>İçeriğin gerçekten tutup tutmadığı</td></tr>
    <tr><td>Kaydetme + paylaşım</td><td>Gönderi istatistikleri</td><td>Satın alma niyetine en yakın sinyal</td></tr>
    <tr><td>Link tıklama oranı</td><td>Affiliate paneli</td><td>Kitlenin harekete geçme kabiliyeti</td></tr>
  </tbody>
</table>
</div>

<blockquote><p>Etkileşim oranı yüksek 8.000 takipçili bir hesap, ölü 80.000 takipçili bir hesaptan daha fazla kazanır. Marka satın alma istiyor, alkış değil.</p></blockquote>

<h2>30 günlük ölçüm disiplini</h2>

<p>Her hafta aynı gün üç sayıyı bir yere yaz: ortalama story erişimi, ortalama Reels izlenmesi, affiliate link tıklaması. Dört hafta sonra hangi içerik türünün tıklama ürettiğini net göreceksin. Sezgiyle değil bu tabloyla ilerle — teklif hazırlarken de elinde kanıt olur.</p>
`,
  },

  {
    slug: "marka-is-birliginde-fiyat-belirleme-medya-kiti",
    title: "Marka İş Birliklerinde Fiyat Nasıl Belirlenir? Medya Kiti Rehberi",
    excerpt:
      "\"Bütçeniz nedir?\" sorusuna hazırlıksız yakalanma. Fiyatlandırma mantığı, medya kiti şablonu ve pazarlıkta işe yarayan cümleler.",
    category: "İş Birlikleri",
    date: "2026-07-28",
    readMinutes: 10,
    icon: "mdi-handshake-outline",
    cover: "linear-gradient(140deg,#c2410c,#7c2d12)",
    body: `
<p>İçerik üreticilerinin en çok para kaybettiği an, ilk teklifi verdikleri andır. Ya çok düşük söylenir ve o rakam sonraki tüm işlerin tavanı olur, ya da gerekçesiz yüksek söylenir ve görüşme kapanır. İkisinin de çaresi aynı: <strong>fiyatı bir hesaba dayandırmak.</strong></p>

<h2>Fiyatı ne belirler?</h2>

<p>Takipçi sayısı fiyatın yalnızca bir girdisidir. Gerçek fiyat şu beş değişkenin bileşkesidir:</p>

<ul>
  <li><strong>Erişim.</strong> Takipçi değil, içeriğin gerçekte kaç kişiye ulaştığı.</li>
  <li><strong>Üretim yükü.</strong> Tek story mi, senaryolu bir Reels mi, stüdyo çekimi mi?</li>
  <li><strong>Kullanım hakları.</strong> Marka içeriği kendi hesabında ya da reklamda kullanacak mı, ne kadar süre?</li>
  <li><strong>Münhasırlık.</strong> Belirli bir süre rakip markayla çalışmama şartı var mı?</li>
  <li><strong>Kalıcılık.</strong> İçerik profilde ne kadar kalacak?</li>
</ul>

<h3>Başlangıç formülü</h3>

<p>Piyasada yaygın kullanılan bir taban hesap şudur:</p>

<blockquote><p><strong>Taban ücret = Ortalama erişim × CPM katsayısı</strong><br>Türkiye'de mikro–orta segment için CPM katsayısı genelde 150–400 TL bandındadır; niş ve satın alma gücü yüksek kitlelerde üstüne çıkar.</p></blockquote>

<p>Bu tabana sonra çarpanlar uygulanır:</p>

<div class="fl-prose__scroll">
<table>
  <thead><tr><th>Ek talep</th><th>Tipik çarpan</th></tr></thead>
  <tbody>
    <tr><td>Reels / senaryolu video</td><td>×1,5 – 2,5</td></tr>
    <tr><td>Marka hesabında yeniden yayın (whitelisting)</td><td>+%30 – 50</td></tr>
    <tr><td>Reklam olarak kullanım (3 ay)</td><td>+%50 – 100</td></tr>
    <tr><td>Kategori münhasırlığı (3 ay)</td><td>+%25 – 50</td></tr>
    <tr><td>Aynı gün teslim / acele iş</td><td>+%25</td></tr>
    <tr><td>3+ içerikli paket</td><td>−%10 – 20 (indirim)</td></tr>
  </tbody>
</table>
</div>

<h2>Medya kitinde ne olmalı?</h2>

<p>Medya kiti iki sayfayı geçmemeli. İçinde şunlar bulunmalı:</p>

<ol>
  <li><strong>Kim olduğun ve nişin.</strong> Tek cümle. "Bütçe dostu ev dekorasyonu yapan 27 yaşında bir içerik üreticisiyim."</li>
  <li><strong>Kitle demografisi.</strong> Yaş aralığı, cinsiyet dağılımı, ilk 3 şehir. Ekran görüntüsü ile.</li>
  <li><strong>Performans sayıları.</strong> Ortalama story erişimi, ortalama Reels izlenmesi, etkileşim oranı — hangi tarih aralığı olduğunu yazarak.</li>
  <li><strong>Geçmiş iş birlikleri.</strong> Marka isimleri ve varsa <em>sonuç</em>: "X markası için 3 story → 1.240 tıklama, 61 satış."</li>
  <li><strong>Paketler ve fiyatlar.</strong> Üç seçenek sun: temel, standart, kapsamlı.</li>
  <li><strong>İletişim ve teslim süresi.</strong></li>
</ol>

<p>Sonuç verisi olan medya kiti, olmayanın iki katı fiyat alır. Affiliate panelindeki tıklama ve satış raporların tam olarak bu işe yarar — <strong>iddiayı kanıta çeviren şey odur.</strong></p>

<h2>Pazarlıkta işe yarayan cümleler</h2>

<h3>"Bütçeniz nedir?" sorusuna</h3>
<p>Rakamı önce sen söyleme. Şunu dene: <em>"Kapsamı netleştirirsek doğru rakamı verebilirim — kaç içerik, hangi format, kullanım hakkı isteniyor mu?"</em></p>

<h3>"Bütçemiz kısıtlı" cevabına</h3>
<p>Fiyatı düşürmek yerine kapsamı daralt: <em>"Bu bütçeyle Reels yerine 3 story + kalıcı koleksiyon linki yapabiliriz."</em> Böylece birim fiyatın korunur.</p>

<h3>"Ürün gönderelim, ücret yok" teklifine</h3>
<p><em>"Ürün karşılığı içerik üretmiyorum, ancak affiliate komisyonu ile performansa dayalı çalışmaya açığım."</em> Bu cevap kapıyı kapatmaz, ilişkiyi ölçülebilir zemine taşır.</p>

<h3>Sonsuz revizyon talebine</h3>
<p>Sözleşmeye baştan yaz: <em>"Fiyata 1 tur revizyon dahildir; ek revizyonlar %20 ücretlendirilir."</em></p>

<h2>Sözleşmede mutlaka olması gerekenler</h2>

<ul>
  <li>Teslim edilecek içeriklerin sayısı, formatı ve yayın tarihi</li>
  <li>Revizyon hakkı adedi</li>
  <li>Kullanım hakkı kapsamı ve süresi (mecra + süre yazılı olmalı)</li>
  <li>Münhasırlık varsa kategorisi ve süresi</li>
  <li>Ödeme vadesi ve fatura süreci</li>
  <li>İptal koşulu (çekim iptal olursa hangi oranda ücret ödenir)</li>
</ul>

<blockquote><p>Sözleşmesiz iş, alacak takibi olmayan iştir. "Bize güvenin" cümlesi bir ödeme vadesi değildir.</p></blockquote>

<h2>Son not</h2>

<p>Fiyatını yükseltmenin en hızlı yolu daha çok takipçi değil, <strong>daha iyi raporlama</strong>dır. Getirdiğin tıklamayı, satışı ve dönüşümü belgeleyebilen bir içerik üreticisi, markanın gözünde bir gider kalemi olmaktan çıkıp bir satış kanalına dönüşür. Fiyat konuşması o noktadan sonra çok daha kolaydır.</p>
`,
  },

  {
    slug: "icerik-kampanyasi-onay-sureci-puf-noktalari",
    title: "İçerik Kampanyasında İlk Turda Onay Almanın Püf Noktaları",
    excerpt:
      "Brief okumaktan taslak yüklemeye, revizyon notlarını doğru anlamaktan istatistik paylaşmaya: onay sürecini hızlandıran pratik kontrol listesi.",
    category: "İş Birlikleri",
    date: "2026-07-19",
    readMinutes: 7,
    icon: "mdi-clipboard-check-outline",
    cover: "linear-gradient(140deg,#ea580c,#9a3412)",
    body: `
<p>Bir içerik kampanyasında süreç dört durakta ilerler: <strong>İçerik → Onay → Yayın → İstatistik.</strong> Deneyimli içerik üreticileri bu döngüyü tek turda tamamlar; yeni başlayanlar ikinci ve üçüncü revizyona takılır. Aradaki fark yeteneğe değil, briefi okuma disiplinine dayanır.</p>

<h2>1. Taslak yüklemeden önce: brief'i maddeleyerek oku</h2>

<p>Brief'i baştan sona okumak yetmez. Bir kenara şu beş başlığı çıkar:</p>

<ul>
  <li><strong>Zorunlu hashtag'ler</strong> — birebir, harfi harfine. "#İşBirliği" ile "#isbirligi" aynı şey değildir.</li>
  <li><strong>Zorunlu etiketler (mention)</strong> — marka hesabı ve varsa alt marka hesabı.</li>
  <li><strong>Zorunlu link</strong> — kampanyanın yönlendirmeni istediği URL.</li>
  <li><strong>Yayın tarihi</strong> — kampanyanın en katı maddesidir; lansmana bağlıdır, esnetilmez.</li>
  <li><strong>Yasaklılar</strong> — söylenmemesi gereken iddialar, gösterilmemesi gereken rakip ürünler.</li>
</ul>

<p>Bu beş maddeyi bir nota yapıştır ve içeriği tamamladıktan sonra tek tek üstünü çiz. Revizyonların büyük çoğunluğu bu listeden bir maddenin atlanmasıdır.</p>

<h2>2. Taslak yüklerken: doğru dosyayı, doğru formatta</h2>

<ul>
  <li>Videoyu <strong>dikey 9:16</strong> ve nihai kurgusuyla yükle. "Kabaca böyle olacak" taslakları onaylanmaz, sadece süreci uzatır.</li>
  <li>Ekran görüntüsü değil, <strong>asıl dosyayı</strong> yükle.</li>
  <li>Kampanya senden ayrıca bir <strong>URL</strong> istiyorsa (örneğin kullandığın ürünün sayfası), taslakla birlikte gir — eksik alan yüklemeyi durdurur.</li>
  <li>Ses telifine dikkat et. Ticari içerikte popüler şarkı kullanımı markanın hesabında sorun çıkarır ve neredeyse her zaman revizyon sebebidir.</li>
</ul>

<h2>3. Revizyon notunu doğru okumak</h2>

<p>Revizyon notu bir eleştiri değil, bir <strong>şartname düzeltmesi</strong>dir. Not geldiğinde şunu yap:</p>

<ol>
  <li>Notu tek tek maddelere böl. Çoğu not iki-üç ayrı talep içerir; biri gözden kaçarsa ikinci revizyon gelir.</li>
  <li>Anlamadığın maddeyi tahmin etme, sor. Yanlış anlaşılmış bir revizyon, hiç yapılmamış bir revizyondan daha fazla zaman kaybettirir.</li>
  <li>Düzeltilmiş içeriği aynı yere yeniden yükle — yeni kayıt açma, süreç aynı satırda devam eder.</li>
</ol>

<blockquote><p>Onay ekibinin en sık yazdığı üç not: "Zorunlu hashtag eksik", "Marka etiketi ilk 3 saniyede görünmüyor", "Ürün ambalajı net okunmuyor". Üçü de yükleme öncesi 30 saniyelik bir kontrolle engellenir.</p></blockquote>

<h2>4. Onay sonrası: yayın tarihi kutsaldır</h2>

<p>Onay ekranında sana büyük puntoyla bir <strong>yayın tarihi</strong> verilir. Bu tarih genelde markanın kampanya takvimine, indirim gününe ya da ürün lansmanına bağlıdır. Erken yayınlamak da geç yayınlamak kadar sorunludur.</p>

<p>Yayınladıktan sonra gönderi linkini sisteme gir. Bu adım kampanyanın "yayınlandı" olarak işaretlenmesini sağlar ve hak edişinin oluşması için gereklidir.</p>

<h2>5. İstatistik adımı: 24 saat kuralı</h2>

<p>Gönderi linkini girdiğin andan itibaren <strong>24 saatlik bir sayaç</strong> başlar. Bu süre dolmadan istatistik yükleme alanı açılmaz — çünkü ilk 24 saat dolmadan alınan story verisi erişimin gerçek büyüklüğünü göstermez.</p>

<p>Süre dolunca dikkat edilecek tek bir şey var:</p>

<ul>
  <li><strong>Profil istatistiğini değil, story/gönderi istatistiğini yükle.</strong> En sık yapılan hata budur. Marka, o içeriğin performansını görmek ister; hesabının genel performansını değil.</li>
  <li>Ekran görüntüsü tam olsun: erişim, gösterim, etkileşim ve link tıklaması aynı karede görünsün.</li>
  <li>Story 24 saat sonra kaybolur; istatistiği <strong>arşivden</strong> alabilirsin, panik yapma.</li>
</ul>

<h2>Tek turda onay kontrol listesi</h2>

<ul>
  <li>☐ Zorunlu hashtag'lerin tamamı, birebir yazımıyla içerikte</li>
  <li>☐ Marka etiketi görünür ve doğru hesap</li>
  <li>☐ Zorunlu link yerinde</li>
  <li>☐ Ürün net görünüyor, ambalaj okunuyor</li>
  <li>☐ Telifsiz ses kullanıldı</li>
  <li>☐ Dikey format, nihai kurgu</li>
  <li>☐ Yasaklı iddia / rakip ürün yok</li>
  <li>☐ İş birliği etiketi mevcut</li>
</ul>

<p>Bu sekiz maddeyi geçen bir taslak, vakaların büyük bölümünde ilk turda onaylanır. Onay hızlandıkça daha çok kampanyaya davet edilirsin — süreç disiplini, doğrudan gelir demektir.</p>
`,
  },

  {
    slug: "influencer-gelirleri-vergi-stopaj-kdv",
    title: "Influencer Gelirleri ve Vergi: Stopaj, KDV ve Kazanç İstisnası",
    excerpt:
      "Sosyal medya kazançları nasıl vergilendirilir? İstisna hangi şartlarda uygulanır, stopaj ne demek, şahıs şirketi ne zaman mantıklı olur?",
    category: "Vergi & Mevzuat",
    date: "2026-07-08",
    readMinutes: 9,
    icon: "mdi-scale-balance",
    cover: "linear-gradient(140deg,#78350f,#451a03)",
    body: `
<p>İçerik üreticiliği artık bir hobi değil, vergi mevzuatında tanımlanmış bir kazanç türü. Bu yazı, kafa karışıklığının en yoğun olduğu üç konuyu — <strong>kazanç istisnası, stopaj ve KDV</strong> — sade biçimde açıklıyor.</p>

<div class="fl-note"><strong>Önemli:</strong> Bu içerik genel bilgilendirme amaçlıdır, mali müşavirlik hizmeti değildir. Tutar sınırları ve oranlar her yıl güncellenir; kendi durumun için mutlaka bir mali müşavire danışmalısın.</p></div>

<h2>Sosyal içerik üreticiliği kazanç istisnası nedir?</h2>

<p>Gelir Vergisi Kanunu'nun mükerrer 20/B maddesi, sosyal paylaşım ağları üzerinden elde edilen kazançlar için özel bir istisna getirdi. Mantığı şu: şartları sağlayan içerik üreticisi, <strong>yıllık gelir vergisi beyannamesi vermez</strong>; vergisi banka aracılığıyla stopaj yoluyla kesilir ve iş biter.</p>

<h3>İstisnadan yararlanma şartları</h3>

<ul>
  <li>Kazanç, <strong>sosyal paylaşım ağları üzerindeki içerik üreticiliğinden</strong> elde edilmiş olmalı (reklam, sponsorluk, bağış, hediye, ücretli abonelik, satış ortaklığı geliri gibi).</li>
  <li>Bir bankada <strong>bu iş için özel bir hesap</strong> açılmalı ve tüm kazançlar münhasıran bu hesaba yatmalı.</li>
  <li>Vergi dairesinden <strong>istisna belgesi</strong> alınmalı ve bankaya ibraz edilmeli.</li>
  <li>Yıllık toplam kazanç, kanunda belirlenen <strong>üst sınırı aşmamalı</strong>. Bu sınır her yıl yeniden değerleme oranıyla güncellenir — güncel rakamı mali müşavirinden ya da GİB'den teyit et.</li>
</ul>

<h3>Şartlar sağlanırsa ne oluyor?</h3>

<p>Banka, hesaba yatan tutar üzerinden <strong>%15 oranında gelir vergisi stopajı</strong> yapar ve senin adına devlete öder. Sen ayrıca beyanname vermezsin. Ödeme dekontunda gördüğün "stopaj" kesintisi budur — <em>platformun aldığı bir komisyon değil, senin adına ödenen vergindir.</em></p>

<h3>Sınır aşılırsa ne olur?</h3>

<p>Yıllık kazanç üst sınırı aştığında istisna o yıl için tamamen ortadan kalkar. Bu durumda kazancın tamamı için yıllık beyanname verilir ve artan oranlı gelir vergisi tarifesi uygulanır. Yıl içinde kesilen stopajlar hesaplanan vergiden mahsup edilir. Sınıra yaklaşıyorsan yılın son çeyreğinde mutlaka planlama yapmalısın.</p>

<h2>KDV tarafı</h2>

<p>İstisna kapsamındaki bu kazançlar için KDV istisnası da uygulanır — yani şartları sağlayan içerik üreticisi KDV mükellefi olmaz, KDV beyannamesi vermez. Ancak istisna kapsamı dışında kalan işler (örneğin danışmanlık, eğitim satışı, prodüksiyon hizmeti) farklı değerlendirilir ve KDV doğurabilir.</p>

<h2>Şahıs şirketi kurmalı mıyım?</h2>

<p>İstisna kapsamındaysan ve sınırın altındaysan şirkete gerek yoktur; sistem zaten daha basittir. Şirket kurmak şu durumlarda anlamlı olur:</p>

<div class="fl-prose__scroll">
<table>
  <thead><tr><th>Durum</th><th>Değerlendirme</th></tr></thead>
  <tbody>
    <tr><td>Kazanç istisna sınırını aşıyor</td><td>Şirket yapısı ve gider yazma avantajlı hale gelir</td></tr>
    <tr><td>İstisna dışı gelirlerin var (danışmanlık, ürün satışı)</td><td>Mükellefiyet zaten gerekir</td></tr>
    <tr><td>Markalar fatura talep ediyor</td><td>Fatura kesebilmek için mükellefiyet gerekir</td></tr>
    <tr><td>Ekipman, ofis, ekip gideri yüksek</td><td>Giderlerin matrahtan düşülmesi büyük fark yaratır</td></tr>
    <tr><td>Düzensiz ve düşük gelir</td><td>Şirketin sabit maliyetleri (muhasebe, BAĞ-KUR) ağır gelebilir</td></tr>
  </tbody>
</table>
</div>

<h2>Ödeme dekontundaki kalemleri okumak</h2>

<p>Hak ediş ekranında birden çok kalem görürsün. Karışmaması için:</p>

<ul>
  <li><strong>Hak ediş / brüt tutar:</strong> Ürettiğin satış veya iş karşılığı oluşan toplam tutar.</li>
  <li><strong>Platform komisyonu:</strong> Aracı kurumun hizmet bedeli. Sözleşmende yazan orandır.</li>
  <li><strong>Stopaj:</strong> Senin adına devlete ödenen gelir vergisi. Kimsenin cebine girmez.</li>
  <li><strong>Tevkifat:</strong> KDV mükellefiysen, KDV'nin bir kısmının alıcı tarafından doğrudan devlete ödenmesi.</li>
  <li><strong>Net ödenecek:</strong> Hesabına geçecek tutar.</li>
</ul>

<h2>Pratik öneriler</h2>

<ol>
  <li><strong>Kazanç hesabını ayrı tut.</strong> İstisnanın ön şartıdır ve yıl sonu takibini kolaylaştırır.</li>
  <li><strong>Her ödemenin dekontunu sakla.</strong> Dijital bir klasör yeterli.</li>
  <li><strong>Yıllık toplamı çeyrek çeyrek takip et.</strong> Sınır sürprizi en pahalı sürprizdir.</li>
  <li><strong>Yurt dışı platform gelirlerini ayrı değerlendir.</strong> Farklı kurallara tabi olabilir.</li>
  <li><strong>Yılda bir kez mali müşavirle otur.</strong> Ödeyeceğin ücret, yapacağın tasarrufun yanında küçük kalır.</li>
</ol>

<p>Vergi, kazancın büyüdüğünü gösteren bir işarettir. Erken öğrenmek, sonradan cezayla öğrenmekten çok daha ucuzdur.</p>
`,
  },

  {
    slug: "link-in-bio-koleksiyon-stratejisi",
    title: "Link-in-Bio Stratejisi: Koleksiyonlarla Satan Bir Profil Kurmak",
    excerpt:
      "Tek bir bio linkiyle onlarca ürünü satılabilir hale getirmek mümkün. Koleksiyon sayfası nasıl kurgulanır, hangi sıralama dönüşüm getirir?",
    category: "Para Kazanma",
    date: "2026-06-30",
    readMinutes: 6,
    icon: "mdi-view-grid-outline",
    cover: "linear-gradient(140deg,#f97316,#c2410c)",
    body: `
<p>Instagram'da gönderi metnine link koyamazsın. Story'ye koyduğun link 24 saat sonra kaybolur. Geriye tek kalıcı alan kalıyor: <strong>bio'daki tek link.</strong> Bu yüzden o linkin ne olduğu, aylık kazancını doğrudan belirler.</p>

<h2>Tekil link vs. koleksiyon sayfası</h2>

<p>Bio'ya tek bir ürün linki koyduğunda, o ürünle ilgilenmeyen herkesi kaybedersin. Koleksiyon sayfası ise takipçiyi bir <strong>vitrine</strong> indirir: ne aradığını kendi seçer.</p>

<div class="fl-prose__scroll">
<table>
  <thead><tr><th></th><th>Tek ürün linki</th><th>Koleksiyon sayfası</th></tr></thead>
  <tbody>
    <tr><td>Ömür</td><td>İçerik gündemden düşünce biter</td><td>Aylarca kazandırmaya devam eder</td></tr>
    <tr><td>Kapsam</td><td>1 ürün</td><td>Onlarca ürün</td></tr>
    <tr><td>Güncelleme</td><td>Bio'yu tekrar tekrar değiştirmek gerekir</td><td>Sayfayı düzenlersin, link sabit kalır</td></tr>
    <tr><td>Ölçüm</td><td>Tek satır veri</td><td>Hangi ürünün tıklandığını görürsün</td></tr>
  </tbody>
</table>
</div>

<h2>Dönüşen bir koleksiyon nasıl kurulur?</h2>

<h3>1. Temaya göre böl, markaya göre değil</h3>
<p>Takipçin "X markası" diye aramaz; "kışlık bot", "hassas cilt bakımı", "ilk ev alışverişi" diye arar. Koleksiyonlarını ihtiyaç etrafında adlandır:</p>
<ul>
  <li>✔ "Sonbahar Gardırop Temeli", "Bütçe Dostu Cilt Bakımı", "Mutfak Başlangıç Seti"</li>
  <li>✘ "Trendyol Favorilerim", "Beğendiklerim 3"</li>
</ul>

<h3>2. İlk beş sıra her şeydir</h3>
<p>Tıklamaların büyük bölümü ilk ekranda kalan ürünlere gider. Bu yüzden en üste şunları koy: <strong>en çok sorulan ürün</strong>, <strong>en uygun fiyatlı ürün</strong> ve <strong>şu an indirimde olan ürün</strong>. Gerisi altta kalabilir.</p>

<h3>3. Her ürüne bir cümle yaz</h3>
<p>Ürün adı yeterli değildir. "3 yıldır aynı ürünü alıyorum, kuru ciltte tek işe yarayan buydu" cümlesi dönüşümü ciddi biçimde artırır. Koleksiyon bir katalog değil, <strong>senin tavsiye listendir.</strong></p>

<h3>4. Ölü ürünü temizle</h3>
<p>Stokta olmayan ya da sayfası kapanan ürün, sayfanın tamamının güvenilirliğini düşürür. Ayda bir kontrol et.</p>

<h2>Trafiği koleksiyona nasıl taşırsın?</h2>

<ul>
  <li><strong>Story'de "profildeki link" demeyi bırak, sticker koy.</strong> Bir adım azaltmak tıklamayı görünür biçimde artırır.</li>
  <li><strong>Öne çıkan story kategorisi aç.</strong> "Favorilerim" adında kalıcı bir highlight, koleksiyona sürekli trafik gönderir.</li>
  <li><strong>Yorumlarda gelen soruya link ile cevap ver.</strong> "Nereden aldın?" sorusu satın alma niyetinin en yüksek olduğu andır.</li>
  <li><strong>Reels açıklamasına koleksiyon adını yaz.</strong> Kullanıcı bio'ya gidince ne arayacağını bilir.</li>
  <li><strong>Bio metnini yönlendirici yap.</strong> "Videodaki tüm ürünler ↓" cümlesi, hiçbir şey yazmamaktan çok daha iyi çalışır.</li>
</ul>

<blockquote><p>Bio linkin bir tabela değil, mağaza vitrinidir. Vitrindeki sıralamayı ayda bir değiştirmek, çoğu içerik üreticisinin hiç dokunmadığı en kolay kazanç kaldıracıdır.</p></blockquote>

<h2>Ayda bir yapılacak 15 dakikalık bakım</h2>

<ol>
  <li>Rapordan en çok tıklanan 5 ürünü çıkar, hepsini en üste taşı.</li>
  <li>Hiç tıklanmayan ürünleri sil ya da açıklamalarını yeniden yaz.</li>
  <li>Sezona uygun 3 yeni ürün ekle.</li>
  <li>Kapağı ve koleksiyon adını güncelle.</li>
  <li>Yeni bir highlight ile sayfayı tekrar duyur.</li>
</ol>

<p>Bu 15 dakika, çoğu hesapta yeni içerik üretmekten daha yüksek getirili bir yatırımdır. Zaten var olan trafiği daha iyi dönüştürmek, sıfırdan trafik yaratmaktan her zaman ucuzdur.</p>
`,
  },
];

export function getPostBySlug(slug: string): IBlogPost | undefined {
  return blogPosts.find((p) => p.slug === slug);
}

/** Aynı kategoriden, mevcut yazı hariç en yeni n kayıt; yetmezse diğerleriyle tamamlar. */
export function getRelatedPosts(slug: string, count = 3) {
  const current = getPostBySlug(slug);
  if (!current) return blogPosts.slice(0, count);
  const sameCategory = blogPosts.filter(
    (p) => p.slug !== slug && p.category === current.category
  );
  const others = blogPosts.filter(
    (p) => p.slug !== slug && p.category !== current.category
  );
  return sameCategory.concat(others).slice(0, count);
}
